import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/axios";
import {
  getSharedApiRequestScope,
  runSharedApiRequest,
} from "@/lib/apiRateLimit";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { useRenderReset } from "@/hooks/useRenderReset";
import type {
  NotificationSummary,
  NotificationState,
  NotificationStatus,
  LayerProcessingUpdate,
  LayerPostgisUpdate,
} from "@/types/notifications";

type NotificationStatusFilter = "all" | "unread" | "read";

interface UseNotificationsOptions {
  pollInterval?: number;
  limit?: number;
  status?: NotificationStatusFilter;
  enableRealtime?: boolean;
  /** Skip the initial notification fetch — useful when you only need websocket callbacks */
  realtimeOnly?: boolean;
  websocketUrl?: string;
  onRealtimeNotification?: (notification: NotificationSummary) => void;
  onProcessingUpdate?: (update: LayerProcessingUpdate) => void;
  onPostgisUpdate?: (update: LayerPostgisUpdate) => void;
}

interface UseNotificationsResult extends NotificationState {
  refresh: () => Promise<void>;
  markAllRead: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
}

const DEFAULT_POLL_INTERVAL = 60_000; // 60s
// While the realtime socket is healthy, created events already deliver the
// list row and the authoritative unread count, so polling only needs to catch
// drift the socket never reports (reads/deletes made on other devices).
// A slow safety net replaces the per-minute cadence.
const CONNECTED_DRIFT_POLL_INTERVAL = 300_000; // 5 minutes
/**
 * How long the first snapshot waits for the socket's first connect. The
 * connect resync then doubles as the first snapshot, so a page load sends
 * one list + count pair instead of two.
 */
export const FIRST_SNAPSHOT_SOCKET_WAIT_MS = 1_500;
const DEFAULT_LIMIT = 10;
const DEFAULT_STATUS: NotificationStatusFilter = "unread";

export const useNotifications = (
  options: UseNotificationsOptions = {}
): UseNotificationsResult => {
  const realtimeDisabledEnv =
    (import.meta.env.VITE_DISABLE_WS as string | undefined)?.toLowerCase() ===
    "true";
  const realtimeEnabledEnv =
    (import.meta.env.VITE_ENABLE_WS as string | undefined)?.toLowerCase() !==
    "false";

  const {
    pollInterval = DEFAULT_POLL_INTERVAL,
    limit = DEFAULT_LIMIT,
    status: statusFilter = DEFAULT_STATUS,
    enableRealtime = true,
    realtimeOnly = false,
    websocketUrl,
    onRealtimeNotification,
    onProcessingUpdate,
    onPostgisUpdate,
  } = options;

  const [state, setState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    loading: true,
    error: null,
  });

  const pollTimer = useRef<number | null>(null);
  const isMounted = useRef(true);
  const fetchGeneration = useRef(0);
  const pendingSnapshot = useRef<{
    events: NotificationSummary[];
    authoritativeUnreadCount: number | null;
    needsCountRefresh: boolean;
    settled: Promise<void>;
  } | null>(null);
  const requestScope = getSharedApiRequestScope();
  useRenderReset(requestScope, () => {
    setState({ notifications: [], unreadCount: 0, loading: true, error: null });
  });

  const fetchNotifications = useCallback(async () => {
    const generation = ++fetchGeneration.current;
    const snapshot = {
      events: [] as NotificationSummary[],
      authoritativeUnreadCount: null as number | null,
      needsCountRefresh: false,
      settled: Promise.resolve(),
    };
    pendingSnapshot.current = snapshot;
    const isCurrent = () =>
      isMounted.current &&
      generation === fetchGeneration.current &&
      getSharedApiRequestScope() === requestScope;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const requests = [
        runSharedApiRequest(
          `notifications:list:${statusFilter}:${Math.min(50, Math.max(1, limit))}`,
          () =>
            api.get<{
              results?: NotificationSummary[];
              data?: NotificationSummary[];
            }>("/notifications/notifications/", {
              params: {
                page_size: Math.min(50, Math.max(1, limit)),
                ...(statusFilter === "all" ? {} : { status: statusFilter }),
              },
            }),
          { rateLimitCooldownMs: 60_000 }
        ),
        runSharedApiRequest(
          "notifications:unread-count",
          () =>
            api.get<{ unread_count: number }>("/notifications/unread-count/"),
          { rateLimitCooldownMs: 60_000 }
        ),
      ] as const;
      // A rejected member of Promise.all must not let a mutation refetch reuse
      // its still-running sibling from the shared request registry.
      snapshot.settled = Promise.allSettled(requests).then(() => undefined);
      const [notificationsResponse, unreadResponse] =
        await Promise.all(requests);

      const rawNotifications =
        notificationsResponse.data.results ??
        notificationsResponse.data.data ??
        (notificationsResponse.data as unknown as NotificationSummary[]) ??
        [];

      const notifications = rawNotifications.slice(0, limit);
      const unreadCount =
        unreadResponse.data.unread_count ?? notifications.length;

      if (!isCurrent()) {
        return;
      }

      setState(() => ({
        // Only overlay events received during this snapshot. Older rows must
        // still be reconciled against the server (including filter changes).
        notifications: [
          ...snapshot.events.filter(
            (notification) =>
              statusFilter === "all" ||
              (statusFilter === "read"
                ? notification.status === "read"
                : notification.status !== "read")
          ),
          ...notifications.filter(
            (notification) =>
              !snapshot.events.some((event) => event.id === notification.id)
          ),
        ].slice(0, limit),
        unreadCount: snapshot.authoritativeUnreadCount ?? unreadCount,
        loading: false,
        error: null,
      }));
      if (snapshot.needsCountRefresh) {
        try {
          // Initialize the usable list first. The optional reconciliation must
          // not hold older rows out of view while its request is pending.
          const refreshed = await runSharedApiRequest(
            "notifications:unread-count:reconcile",
            () =>
              api.get<{ unread_count: number }>("/notifications/unread-count/"),
            { rateLimitCooldownMs: 60_000 }
          );
          if (isCurrent()) {
            setState((prev) => ({
              ...prev,
              unreadCount:
                snapshot.authoritativeUnreadCount ??
                refreshed.data.unread_count ??
                prev.unreadCount,
            }));
          }
        } catch {
          // Keep the usable list and server count if this optional refresh
          // fails; an uninitialized local increment is not a better total.
        }
      }
    } catch (error) {
      if (!isCurrent()) {
        return;
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : "Unable to load notifications",
      }));
    } finally {
      void snapshot.settled.then(() => {
        if (pendingSnapshot.current === snapshot)
          pendingSnapshot.current = null;
      });
    }
  }, [limit, statusFilter, requestScope]);

  const latestView = useRef({ statusFilter, fetchNotifications });
  useEffect(() => {
    latestView.current = { statusFilter, fetchNotifications };
  }, [statusFilter, fetchNotifications]);

  const invalidateSnapshotAfterMutation = useCallback(() => {
    const snapshot = pendingSnapshot.current;
    if (!snapshot) return;
    const generation = ++fetchGeneration.current;
    // Starting immediately would deduplicate onto the pre-mutation response.
    // Only the latest mutation needs to replace the drained snapshot.
    void snapshot.settled.then(() => {
      if (
        isMounted.current &&
        generation === fetchGeneration.current &&
        getSharedApiRequestScope() === requestScope
      ) {
        void latestView.current.fetchNotifications();
      }
    });
  }, [requestScope]);

  const handleRealtimeMessage = useCallback(
    (payload: Record<string, unknown>) => {
      if (!isMounted.current) {
        return;
      }

      if (payload?.event === "notification.created" && payload.notification) {
        const incoming = payload.notification as NotificationSummary;
        if (pendingSnapshot.current) {
          pendingSnapshot.current.authoritativeUnreadCount =
            typeof payload.unread_count === "number"
              ? payload.unread_count
              : null;
          pendingSnapshot.current.needsCountRefresh =
            typeof payload.unread_count !== "number";
          pendingSnapshot.current.events = [
            incoming,
            ...pendingSnapshot.current.events.filter(
              (event) => event.id !== incoming.id
            ),
          ].slice(0, limit);
        }
        setState((prev) => {
          const alreadyPresent = prev.notifications.some(
            (notification) => notification.id === incoming.id
          );
          const existing = prev.notifications.filter(
            (notification) => notification.id !== incoming.id
          );
          const matchesFilter =
            statusFilter === "all" ||
            (statusFilter === "read"
              ? incoming.status === "read"
              : incoming.status !== "read");
          const notifications = (
            matchesFilter ? [incoming, ...existing] : existing
          ).slice(0, limit);
          const unreadCount =
            typeof payload.unread_count === "number"
              ? payload.unread_count
              : prev.unreadCount +
                (alreadyPresent || incoming.status === "read" ? 0 : 1);

          return {
            ...prev,
            notifications,
            unreadCount,
            loading: false,
          };
        });
        onRealtimeNotification?.(incoming);
        return;
      }

      if (payload?.event === "layer.processing") {
        onProcessingUpdate?.(payload as unknown as LayerProcessingUpdate);
      }

      if (payload?.event === "layer.postgis") {
        onPostgisUpdate?.(payload as unknown as LayerPostgisUpdate);
      }
    },
    [
      limit,
      statusFilter,
      onPostgisUpdate,
      onProcessingUpdate,
      onRealtimeNotification,
    ]
  );

  const realtimeActive =
    enableRealtime && !realtimeDisabledEnv && realtimeEnabledEnv;
  const { connectionState } = useRealtimeEvents({
    enabled: realtimeActive,
    websocketUrl,
    onEvent: handleRealtimeMessage,
  });
  const realtimeHealthy = realtimeActive && connectionState === "connected";

  const refresh = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/notifications/mark_all_read/");
      if (!isMounted.current || getSharedApiRequestScope() !== requestScope) {
        return;
      }

      invalidateSnapshotAfterMutation();

      setState((prev) => ({
        ...prev,
        notifications: [],
        unreadCount: 0,
        loading: false,
      }));
    } catch (error) {
      if (!isMounted.current || getSharedApiRequestScope() !== requestScope) {
        return;
      }
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : "Unable to mark notifications as read",
      }));
    }
  }, [requestScope, invalidateSnapshotAfterMutation]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await api.post(
          `/notifications/notifications/${notificationId}/mark_read/`
        );
        if (!isMounted.current || getSharedApiRequestScope() !== requestScope) {
          return;
        }

        invalidateSnapshotAfterMutation();

        setState((prev: NotificationState): NotificationState => {
          const wasUnread = prev.notifications.some(
            (notification) =>
              notification.id === notificationId &&
              notification.status !== "read"
          );
          const notifications: NotificationSummary[] =
            prev.notifications.flatMap(
              (notification): NotificationSummary[] => {
                if (notification.id !== notificationId) {
                  return [notification];
                }

                if (latestView.current.statusFilter === "unread") {
                  return [];
                }

                return [
                  {
                    ...notification,
                    status: "read" as NotificationStatus,
                    read_at: new Date().toISOString(),
                  },
                ];
              }
            );

          const unreadCount = Math.max(
            prev.unreadCount - (wasUnread ? 1 : 0),
            0
          );
          return { ...prev, notifications, unreadCount, loading: false };
        });
      } catch (error) {
        if (!isMounted.current || getSharedApiRequestScope() !== requestScope) {
          return;
        }
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Unable to mark notification as read",
        }));
      }
    },
    [requestScope, invalidateSnapshotAfterMutation]
  );

  // Read realtime health through a ref so socket state changes do not tear
  // down the poll cycle (which used to refetch immediately on every
  // connect/disconnect, on top of the reconnect resync below).
  const realtimeHealthyRef = useRef(realtimeHealthy);
  // The first snapshot is owed until it runs, either on the socket's first
  // connect or when FIRST_SNAPSHOT_SOCKET_WAIT_MS passes without one.
  const firstSnapshotOwed = useRef(realtimeActive && !realtimeOnly);
  const firstSnapshotTimer = useRef<number | null>(null);
  const clearFirstSnapshotTimer = () => {
    if (firstSnapshotTimer.current !== null) {
      window.clearTimeout(firstSnapshotTimer.current);
      firstSnapshotTimer.current = null;
    }
  };
  // Time accumulated by poll ticks since the last snapshot fetch.
  const msSinceLastFetch = useRef(0);
  useEffect(() => {
    realtimeHealthyRef.current = realtimeHealthy;
  }, [realtimeHealthy]);

  // Poll at the requested cadence only while realtime is unavailable. A
  // connected socket pushes created/read-relevant state, so polling degrades
  // to the slow drift-correction interval instead of repeating every minute.
  useEffect(() => {
    isMounted.current = true;
    const fetchSnapshot = () => {
      // Any snapshot settles the first one still owed.
      firstSnapshotOwed.current = false;
      clearFirstSnapshotTimer();
      msSinceLastFetch.current = 0;
      void fetchNotifications();
    };
    if (!realtimeOnly) {
      if (firstSnapshotOwed.current) {
        firstSnapshotTimer.current = window.setTimeout(() => {
          firstSnapshotTimer.current = null;
          fetchSnapshot();
        }, FIRST_SNAPSHOT_SOCKET_WAIT_MS);
      } else {
        fetchSnapshot();
      }
    }
    if (realtimeOnly || pollInterval <= 0) {
      return () => {
        isMounted.current = false;
        fetchGeneration.current += 1;
        clearFirstSnapshotTimer();
      };
    }

    const driftInterval = Math.max(pollInterval, CONNECTED_DRIFT_POLL_INTERVAL);
    const tick = () => {
      msSinceLastFetch.current += pollInterval;
      if (document.hidden) return;
      if (
        realtimeHealthyRef.current &&
        msSinceLastFetch.current < driftInterval
      ) {
        return;
      }
      fetchSnapshot();
    };
    pollTimer.current = window.setInterval(tick, pollInterval);

    let wasHidden = document.hidden;
    const onVisibilityChange = () => {
      if (document.hidden === wasHidden) return;
      wasHidden = document.hidden;
      // Returning to a visible page always deserves a fresh snapshot.
      if (!wasHidden) fetchSnapshot();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      isMounted.current = false;
      fetchGeneration.current += 1;
      clearFirstSnapshotTimer();
      if (pollTimer.current !== null) window.clearInterval(pollTimer.current);
      pollTimer.current = null;
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [fetchNotifications, pollInterval, realtimeOnly]);

  // Anything pushed while the socket was down is invisible to the client, so
  // every transition into a healthy connection performs one resync.
  const wasRealtimeHealthy = useRef(false);
  useEffect(() => {
    const justConnected = realtimeHealthy && !wasRealtimeHealthy.current;
    wasRealtimeHealthy.current = realtimeHealthy;
    if (justConnected && !realtimeOnly) {
      // Also serves as the first snapshot when the socket connects in time.
      firstSnapshotOwed.current = false;
      clearFirstSnapshotTimer();
      msSinceLastFetch.current = 0;
      void fetchNotifications();
    }
  }, [realtimeHealthy, realtimeOnly, fetchNotifications]);

  const value = useMemo(
    () => ({
      ...state,
      refresh,
      markAllRead,
      markAsRead,
    }),
    [state, refresh, markAllRead, markAsRead]
  );

  return value;
};
